import React, { useEffect, useState } from "react"
import { ScrollView, View } from "react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import { SubjectChip } from "./SubjectChip"

/**
 * Choix des matières, en modale — comme les créneaux et la date.
 *
 * Le carrousel horizontal qui la précédait ne pouvait pas montrer une sélection
 * de quatre matières : le parent devait faire défiler pour se rappeler ce qu'il
 * avait coché, et ne se rendait donc pas compte de son propre choix. La ligne
 * qui ouvre cette modale, elle, les liste toutes d'un coup.
 *
 * La sélection ne remonte qu'à la VALIDATION : fermer en croix rend la
 * sélection précédente, comme la modale de créneaux.
 *
 * Les matières s'y présentent en grille qui passe à la ligne, PAS en carrousel
 * horizontal : dans une modale on a toute la largeur et de la hauteur, et
 * cacher la moitié des choix derrière un défilement latéral annulerait la seule
 * raison d'être de cette modale — tout voir d'un coup.
 */
const SubjectsPickerModal = ({
    isVisible,
    onClose,
    onValidate,
    options = [],
    initialSubjects = [],
    // Plafond APPLIQUÉ : celui du serveur croisé avec le volume acheté.
    maxSubjects = null,
    // Plafond ANNONCÉ, qui n'est pas toujours le même. On ne dit le nombre que
    // lorsqu'il découle du volume acheté (au lycée, une matière veut son
    // heure) : c'est alors une conséquence du choix du parent. Le plafond
    // technique de 10, lui, reste muet — l'écrire inviterait à s'en approcher
    // alors qu'on veut une, deux, trois matières.
    maxSubjectsLabel = null,
    // Vrai quand le volume ne couvre pas toutes les matières : au collège
    // l'accompagnement devient du suivi de devoirs, et on le dit.
    homeworkSupportFrom = null,
}) => {
    const [selected, setSelected] = useState(initialSubjects)

    useEffect(() => {
        if (isVisible) setSelected(initialSubjects)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible])

    const count = selected.length
    const enough = count > 0
    const isHomeworkSupport =
        homeworkSupportFrom !== null && count > homeworkSupportFrom

    return (
        <ModalSkeleton
            isVisible={isVisible}
            onClose={onClose}
            title="Vos matières"
            scrollable={false}
        >
            <TextM style={styles.helper} wrap>
                Les matières sur lesquelles porte l&apos;accompagnement
                {maxSubjectsLabel
                    ? ` — ${maxSubjectsLabel} au maximum pour ${maxSubjectsLabel} h par semaine`
                    : ""}
                .
            </TextM>

            <ScrollView
                style={styles.grid}
                contentContainerStyle={styles.gridContent}
                showsVerticalScrollIndicator={false}
            >
                {options.map((option) => {
                    const isSelected = selected.includes(option.value)
                    const atMax =
                        maxSubjects !== null && selected.length >= maxSubjects

                    return (
                        <SubjectChip
                            key={option.value}
                            label={option.label}
                            iconUri={option.labelIconImage}
                            isSelected={isSelected}
                            disabled={!isSelected && atMax}
                            onPress={() =>
                                setSelected(
                                    isSelected
                                        ? selected.filter(
                                              (id) => id !== option.value
                                          )
                                        : [...selected, option.value]
                                )
                            }
                        />
                    )
                })}
            </ScrollView>

            <View style={styles.footer}>
                <TextM style={styles.count} wrap>
                    {count === 0
                        ? "Aucune matière sélectionnée"
                        : `${count} matière${count > 1 ? "s" : ""} sélectionnée${count > 1 ? "s" : ""}`}
                </TextM>
                {isHomeworkSupport ? (
                    <TextM style={styles.notice} wrap>
                        Au-delà de {homeworkSupportFrom} matière
                        {homeworkSupportFrom > 1 ? "s" : ""}, le tuteur fera de
                        l&apos;aide aux devoirs sur l&apos;ensemble de ces
                        matières.
                    </TextM>
                ) : null}
                <FilledButtonM
                    label={
                        enough
                            ? "Valider mes matières"
                            : "Sélectionnez une matière"
                    }
                    onPress={() => onValidate(selected)}
                    disabled={!enough}
                    color={
                        enough ? theme.colors.primary : theme.colors.lightMore
                    }
                    style={{ width: "100%", marginBottom: 0 }}
                />
            </View>
        </ModalSkeleton>
    )
}

const styles = {
    helper: { fontSize: 13, color: theme.colors.grey, marginBottom: 12 },
    // Hauteur bornée : la grille scrolle VERTICALEMENT, l'en-tête et le pied
    // restent fixes.
    grid: { maxHeight: 340 },
    gridContent: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 10,
        paddingBottom: 4,
    },
    footer: { paddingTop: 12, gap: 8 },
    count: {
        fontSize: 13,
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
        textAlign: "center",
    },
    notice: { fontSize: 12, color: theme.colors.grey, textAlign: "center" },
}

export default SubjectsPickerModal
